import type {
  AssetListQuery,
  AssetListResponse,
  AssetView,
  CanvasDocumentV3 as CanvasDocument
} from "@seedance/contracts";

export interface AssetReferenceSearchState {
  query: string;
  assets: AssetView[];
  nextCursor: string | undefined;
  status: "idle" | "loading" | "ready" | "error";
}

export interface AssetReferenceSearchController {
  getState(): AssetReferenceSearchState;
  search(query: string): Promise<void>;
  loadMore(): Promise<void>;
}

export interface ComposerCandidate {
  source: "canvas" | "library";
  asset: AssetView;
  nodeId?: string;
  edgeId?: string | null;
}

function compareAssets(left: AssetView, right: AssetView): number {
  return left.displayName.localeCompare(right.displayName, "zh-CN") || left.id.localeCompare(right.id);
}

function matchesQuery(asset: AssetView, query: string): boolean {
  return asset.displayName.toLocaleLowerCase("zh-CN").includes(query.toLocaleLowerCase("zh-CN"));
}

export function groupComposerCandidates({
  document,
  knownAssets,
  libraryAssets,
  query,
  seedanceNodeId
}: {
  document: CanvasDocument;
  knownAssets: readonly AssetView[];
  libraryAssets: readonly AssetView[];
  query: string;
  seedanceNodeId?: string;
}): { canvas: ComposerCandidate[]; library: ComposerCandidate[] } {
  const normalizedQuery = query.trim();
  const assetsById = new Map(knownAssets.map((asset) => [asset.id, asset]));
  const canvas = document.nodes
    .filter((node) => node.kind === "image" || node.kind === "video" || node.kind === "audio")
    .flatMap((node) => {
      const asset = assetsById.get(node.assetId);
      if (!asset || asset.kind !== node.kind || !matchesQuery(asset,normalizedQuery)) return [];
      const edges = document.edges.filter(edge => edge.sourceNodeId === node.id && edge.targetNodeId === seedanceNodeId);
      return (edges.length ? edges.map(edge => edge.id) : [null]).map(edgeId => ({source:"canvas" as const,asset,nodeId:node.id,edgeId}));
    })
    .sort((left, right) => compareAssets(left.asset, right.asset) || left.nodeId!.localeCompare(right.nodeId!));
  const canvasAssetIds = new Set(canvas.map((candidate) => candidate.asset.id));
  const library = [...libraryAssets]
    .filter((asset) => !canvasAssetIds.has(asset.id) && matchesQuery(asset, normalizedQuery))
    .sort(compareAssets)
    .map((asset) => ({ source: "library" as const, asset }));
  return { canvas, library };
}

function listQuery(query: string, cursor?: string): AssetListQuery {
  const normalized = query.trim();
  return {
    view: "library",
    ...(normalized ? { q: normalized } : {}),
    ...(cursor ? { cursor } : {}),
    limit: 24
  };
}

export function createAssetReferenceSearchController({
  listAssets
}: {
  listAssets(query: AssetListQuery): Promise<AssetListResponse>;
}): AssetReferenceSearchController {
  let state: AssetReferenceSearchState = {
    query: "",
    assets: [],
    nextCursor: undefined,
    status: "idle"
  };
  let generation = 0;

  const snapshot = (): AssetReferenceSearchState => ({
    ...state,
    assets: [...state.assets]
  });

  const fetchPage = async (
    query: string,
    requestGeneration: number,
    cursor: string | undefined,
    append: boolean
  ): Promise<void> => {
    state = { ...state, query, status: "loading" };
    try {
      const response = await listAssets(listQuery(query, cursor));
      if (requestGeneration !== generation) return;
      state = {
        query,
        assets: append ? [...state.assets, ...response.assets] : [...response.assets],
        nextCursor: response.nextCursor,
        status: "ready"
      };
    } catch {
      if (requestGeneration !== generation) return;
      state = { ...state, query, status: "error" };
    }
  };

  return {
    getState: snapshot,
    search(query) {
      const requestGeneration = ++generation;
      return fetchPage(query, requestGeneration, undefined, false);
    },
    loadMore() {
      if (state.nextCursor === undefined) return Promise.resolve();
      return fetchPage(state.query, generation, state.nextCursor, true);
    }
  };
}
